/**
 * Shapes returned by `Survey/GetPreviewDataFlat`.
 *
 * The endpoint replaces `Survey/GetPreviewData` for rendering/filling a survey: instead of the
 * recursive `surveyItems[] -> innerItems[]` tree it returns the whole survey as one flat, ordered
 * list, resolved into a single language server side. It exists because the nested endpoint issued
 * ~90 DB round trips for a large survey.
 *
 * This project has no TypeScript or PropTypes, so these are JSDoc typedefs only - they document
 * the payload for readers and give editors autocomplete, and are erased at build time.
 */

/** Discriminator values of `item.itemType`. */
export const FLAT_ITEM_TYPE = {
   SECTION: "Section",
   QUESTION: "Question"
};

/**
 * `statusCode` values that arrive inside the response envelope. The API returns some of these with
 * HTTP 200, so a client must look at the envelope and not only at the transport status.
 */
export const PREVIEW_STATUS_CODE = {
   OK: 200,
   /** Survey does not exist. Arrives as HTTP 200 with `isSuccess: true` and `data: null`. */
   NO_CONTENT: 204,
   /** Survey is archived and the caller is anonymous. Arrives as HTTP 200. */
   UNAUTHORIZED: 401,
   BAD_REQUEST: 400
};

/**
 * @typedef {Object} ResponseEnvelope
 * @property {string|null} message
 * @property {boolean} isSuccess
 * @property {string[]} errors
 * @property {*} data
 * @property {number} statusCode
 */

/**
 * @typedef {Object} FlatSurveyLanguageMap
 * @property {string} id
 * @property {string} languageId
 * @property {string} name
 * @property {string} code           Two letter code, e.g. "EN". Not present on the old endpoint.
 * @property {boolean} isBaseLanguage
 * @property {number} translateStatus
 */

/**
 * @typedef {Object} FlatChoiceFilterQuestion
 * @property {string} id
 * @property {string} name
 * @property {string} title
 * @property {number} questionType
 */

/**
 * One cascading-select filter attached to a choice.
 *
 * @typedef {Object} FlatChoiceFilter
 * @property {string} choiceFilterId
 * @property {string} listName
 * @property {string} name
 * @property {FlatChoiceFilterQuestion[]} questions
 */

/**
 * @typedef {Object} FlatChoice
 * @property {string} id
 * @property {string} listName
 * @property {string} name
 * @property {string|null} label            Already translated; null when no label exists.
 * @property {string|null} labelLanguageId  Language `label` actually came from.
 * @property {number} order
 * @property {number|null} itemType         0 = Question, 1 = Section.
 * @property {string|null} objectName
 * @property {string|null} filter_Param_Name
 * @property {string|null} filter_Param_Value
 * @property {boolean|null} displayAsAnswerChoice
 * @property {boolean|null} displayAsComment
 * @property {boolean|null} isOther
 * @property {boolean|null} noneOfTheAbove
 * @property {string|null} emoji
 * @property {FlatChoiceFilter[]} choiceFilters
 */

/**
 * Matrix / rating column.
 *
 * @typedef {Object} FlatSecondChoice
 * @property {string} id
 * @property {string} name
 * @property {string|null} label
 * @property {string|null} labelLanguageId
 */

/**
 * @typedef {Object} FlatSkipLogicResultChoice
 * @property {string} id
 * @property {string} listName
 * @property {string} name
 * @property {number} itemType
 * @property {string} objectName
 * @property {number} order
 */

/**
 * @typedef {Object} FlatSkipLogic
 * @property {string} id
 * @property {number} type
 * @property {string} itemName
 * @property {number} logicalOperator
 * @property {FlatSkipLogicResultChoice[]} resultChoices
 */

/**
 * @typedef {Object} FlatRange
 * @property {string} id
 * @property {number} from
 * @property {number} to
 * @property {string} firstLabel
 * @property {string} secondLabel
 */

/**
 * One entry of `data.items` - either a section or a question.
 *
 * The array order IS the render order: `order` is per-parent and the same value can appear on both
 * a section and a question in the same parent, so sorting by it corrupts the survey. Nesting comes
 * from `parentSectionId` / `depth`, and a section's whole subtree is the consecutive run of items
 * right after it whose `depth` is greater than the section's own.
 *
 * @typedef {Object} FlatSurveyItem
 * @property {"Section"|"Question"} itemType
 * @property {string} id
 * @property {number} index            0-based position in `data.items`.
 * @property {number} order            Order inside its parent (survey or section).
 * @property {number} depth            0 = directly on the survey.
 * @property {string|null} parentSectionId
 * @property {string|null} surveyId
 * @property {number|null} surveyOrder
 * @property {string} name             Technical xlsform name.
 * @property {string|null} title       Translated label. Null on a section without a label.
 * @property {string|null} titleLanguageId  Language `title` came from; null when it fell back to
 *                                          the question's raw Title column.
 * @property {string|null} description
 * @property {string|null} relevant
 * @property {number|null} maxScore
 * @property {string|null} appearance
 * @property {FlatSkipLogic[]} skipLogics
 * @property {boolean} isChildSection  Section only: sits inside another section.
 * @property {string|null} repeat_count Section only: repeat group count expression.
 * @property {number|null} repeatGroup  Section only.
 * @property {number|null} questionType Question only. Same numeric codes as the old endpoint.
 * @property {boolean} isRequired       Question only.
 * @property {string|null} hint         Question only, translated.
 * @property {string|null} constraintMessage Question only, translated.
 * @property {string|null} default
 * @property {string|null} calculation
 * @property {string|null} constraint
 * @property {string|null} parameters
 * @property {string|null} choiceFilter
 * @property {string|null} trigger
 * @property {string|null} given_name
 * @property {number|null} accuracyThreshold
 * @property {boolean} hasEmoji
 * @property {string|null} imageUrl
 * @property {number|null} maxChoices
 * @property {FlatRange|null} range
 * @property {FlatChoice[]} choices
 * @property {FlatSecondChoice[]} secondChoices
 */

/**
 * `data` of a successful response: the survey header plus the flat item list.
 *
 * @typedef {Object} FlatPreviewData
 * @property {string} id
 * @property {string} name
 * @property {string|null} description
 * @property {boolean} toggleLocation
 * @property {string|null} manager     Can be null when the manager user no longer exists.
 * @property {number} surveyStatusId   0 = Editing, 1 = Deployed, 2 = Archived.
 * @property {boolean} canSubmitSurvey
 * @property {string} languageId       Language the texts are in.
 * @property {string} baseLanguageId   Language used as fallback.
 * @property {FlatSurveyLanguageMap[]} surveyLanguageMaps
 * @property {FlatSurveyItem[]} items
 */
